/**
 * Cómo se lee un registro de la auditoría: quién, qué, sobre qué y con qué detalle. Lo que la consola
 * no conoce (un tipo o una acción nuevos) se muestra igual, con su nombre técnico, nunca se oculta.
 */
import type { RegistroAuditoria } from "@/shared/api";
import { t } from "@/shared/i18n";

const a = t.auditoria;

export interface Lectura {
  readonly quien: string;
  readonly que: string;
  readonly sobre: string;
  readonly detalle: string;
}

/** Correo de un usuario del portal a partir de su id; la auditoría guarda solo el id. */
export type CorreoDe = (usuarioId: string) => string | undefined;

const PREFIJO_INSTITUCION = "institucion:";

export function describirRecurso(recurso: string | null): string {
  if (!recurso) return "";
  const i = recurso.indexOf(":");
  const tipo = recurso.slice(0, i);
  const valor = recurso.slice(i + 1);
  const lista = valor.split(",").join(", ");
  switch (tipo) {
    case "embarques":
      return a.recursos.embarques(lista);
    case "embarque":
      return a.recursos.embarque(valor);
    case "documento": {
      const [clave = "", id = ""] = valor.split("/");
      return a.recursos.documento(clave, id);
    }
    case "facturas":
      return a.recursos.facturas(lista);
    case "cargos":
      return a.recursos.cargos(valor);
    case "destinatarios":
      return a.recursos.destinatarios(lista);
    default:
      return recurso;
  }
}

function valorLegible(v: string): string {
  if (v in t.perfiles) return t.perfiles[v as keyof typeof t.perfiles];
  if (v in t.relaciones) return t.relaciones[v as keyof typeof t.relaciones];
  return v;
}

export function describirDetalle(detalle: Readonly<Record<string, string | null>>): string {
  return Object.entries(detalle)
    .filter((x): x is [string, string] => typeof x[1] === "string" && x[1] !== "")
    .map(([k, v]) => `${a.campos[k] ?? k}: ${valorLegible(v)}`)
    .join(" · ");
}

export function describir(r: RegistroAuditoria, correoDe: CorreoDe): Lectura {
  const sobreInstitucion = r.usuarioId.startsWith(PREFIJO_INSTITUCION);
  const sujeto = sobreInstitucion
    ? a.institucion(r.usuarioId.slice(PREFIJO_INSTITUCION.length))
    : (correoDe(r.usuarioId) ?? r.usuarioId);
  if (r.tipo === "admin") {
    return {
      quien: r.actor === "tarea-diaria" ? a.tareaDiaria : (r.actor ?? ""),
      que: (r.accion && a.acciones[r.accion]) ?? a.otro(r.accion ?? ""),
      sobre: sujeto,
      detalle: describirDetalle(r.detalle),
    };
  }
  return {
    quien: sujeto,
    que: a.tipos[r.tipo] ?? a.otro(r.tipo),
    sobre: describirRecurso(r.recurso),
    detalle: describirDetalle(r.detalle),
  };
}
